import React from 'react';

import { Button, Flex, Modal } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError, HttpStatusCode } from 'axios';
import { InviteUserForm } from 'pages/Organisation/InviteUserForm';
import { useTranslation } from 'react-i18next';
import { NotificationService } from 'services/notification-service';
import { OrganisationService } from 'services/organisation-service';
import { ALL_USERS_INFO_QUERY_KEY } from 'shared/constants/query-keys';
import { User, UserAccess } from 'shared/interfaces/User';
import { emailValidator, requiredInputValidator } from 'shared/utils/form-validators';

interface InviteUserModalProps {
  isModalOpened: boolean;
  closeModal: () => void;
}

export function InviteUserModal({ isModalOpened, closeModal }: InviteUserModalProps) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const userInviteForm = useForm({
    initialValues: {
      email: '',
      firstName: '',
      lastName: '',
      role: UserAccess.MEMBER.toString()
    },
    validate: {
      email: (value) => requiredInputValidator(value) || emailValidator(value),
      firstName: (value) => requiredInputValidator(value),
      lastName: (value) => requiredInputValidator(value)
    }
  });

  const closeAndResetForm = () => {
    userInviteForm.reset();
    closeModal();
  };

  const createUserMutation = useMutation({
    mutationFn: OrganisationService.inviteUser,
    onSuccess: (newUser: User) => {
      queryClient.setQueryData([ALL_USERS_INFO_QUERY_KEY], (existingUsers: User[] = []) => [...existingUsers, newUser]);
      NotificationService.showResult(true, t('FEED_BACK.USERS.SUCCESS.CREATE'));
      closeAndResetForm();
    },
    onError: (error: AxiosError) => {
      const failureKey =
        error.response?.status == HttpStatusCode.Conflict
          ? 'FEED_BACK.USERS.FAILURE.CREATE.CONFLICT'
          : 'FEED_BACK.USERS.FAILURE.CREATE.UNKNOWN';
      NotificationService.showResult(false, failureKey, error);
    }
  });

  const handleInviteUsers = () => {
    const result = userInviteForm.validate();
    if (result.hasErrors) return;

    createUserMutation.mutate({
      email: userInviteForm.values.email.trim(),
      firstName: userInviteForm.values.firstName.trim(),
      lastName: userInviteForm.values.lastName.trim(),
      role: parseInt(userInviteForm.values.role) as UserAccess
    });
  };

  return (
    <Modal title={t('INVITE_USER.TITLE')} size={775} opened={isModalOpened} onClose={closeAndResetForm}>
      <Flex w="100%" display="flex" justify="center" align="center" direction="column">
        <InviteUserForm form={userInviteForm} />
      </Flex>

      <Flex display="flex" justify="center" align="center" direction="column">
        <Button
          mt={30}
          style={{ width: '200px' }}
          loading={createUserMutation.isLoading}
          onClick={(event) => {
            event.stopPropagation();
            handleInviteUsers();
          }}>
          {t('INVITE_USER.ACTION')}
        </Button>
      </Flex>
    </Modal>
  );
}
